/**
 * PackageTimeline Component
 * Renders a Learning Package activity timeline using the Ant Design Timeline:
 * a vertical line with one colored dot per activity group (blue = learning path,
 * purple = quiz collection, cyan = training profile). Each group starts with a
 * bold clickable header that opens the item for playing, followed by the
 * detailed events inside it (clicks, opens, completions, scores).
 *
 * Used by the public package detail page and the creator participant drawer.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Tag, Timeline, Tooltip } from 'antd';
import {
  AimOutlined,
  ApartmentOutlined,
  AppstoreAddOutlined,
  CheckCircleFilled,
  ExperimentOutlined,
  FilePdfOutlined,
  FormOutlined,
  LinkOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  RocketOutlined,
  SoundOutlined,
  TrophyOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { PackageItemTypes } from '../../services/learningPackagesService';
import styles from './PackageTimeline.module.css';

const TYPE_COLORS = {
  [PackageItemTypes.LearningPath]: '#1890ff',
  [PackageItemTypes.QuizCollection]: '#722ed1',
  [PackageItemTypes.TrainingProfile]: '#13c2c2',
};

const typeIcon = (itemType) => {
  switch (itemType) {
    case PackageItemTypes.LearningPath:
      return <ApartmentOutlined style={{ color: TYPE_COLORS[itemType] }} />;
    case PackageItemTypes.QuizCollection:
      return <QuestionCircleOutlined style={{ color: TYPE_COLORS[itemType] }} />;
    case PackageItemTypes.TrainingProfile:
      return <ExperimentOutlined style={{ color: TYPE_COLORS[itemType] }} />;
    default:
      return <AppstoreAddOutlined />;
  }
};

const eventIcon = (eventType) => {
  switch (eventType) {
    case 'quiz-completed':
      return <CheckCircleFilled style={{ color: '#52c41a' }} />;
    case 'quest-completed':
      return <TrophyOutlined style={{ color: '#d4a017' }} />;
    case 'path-visit':
      return <ApartmentOutlined />;
    case 'element-click':
      return <AimOutlined />;
    case 'pdf-open':
      return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
    case 'audio-open':
      return <SoundOutlined />;
    case 'video-open':
      return <VideoCameraOutlined />;
    case 'link-open':
      return <LinkOutlined />;
    case 'quiz-open':
      return <QuestionCircleOutlined />;
    case 'survey-open':
      return <FormOutlined />;
    case 'training-profile-open':
    case 'item-open':
      return <RocketOutlined />;
    default:
      return <RightOutlined />;
  }
};

/**
 * Renders the grouped package activity timeline.
 * @param {Object} props - Component props
 * @param {Array} props.groups - Timeline groups from the my-stats or participant-details endpoints
 * @param {number} [props.maxGroups] - Maximum groups to render
 * @param {number} [props.maxEventsPerGroup] - Maximum events shown per group before the expand link
 * @param {'newest'|'oldest'} [props.order] - Display order of the groups
 * @returns {JSX.Element} Timeline
 */
const PackageTimeline = ({ groups, maxGroups = 20, maxEventsPerGroup = 8, order = 'newest' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Stable per-group keys so expansion survives order flips.
  const [expandedGroupKeys, setExpandedGroupKeys] = useState(() => new Set());

  const toggleExpanded = (groupKey) => {
    setExpandedGroupKeys((previous) => {
      const next = new Set(previous);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const eventLabel = (eventType) => {
    switch (eventType) {
      case 'quiz-completed':
        return t('learningPackages.eventQuizCompleted', 'Completed quiz');
      case 'quest-completed':
        return t('learningPackages.eventQuestCompleted', 'Completed quest');
      case 'path-visit':
        return t('learningPackages.eventPathVisit', 'Visited the path');
      case 'element-click':
        return t('learningPackages.eventElementClick', 'Clicked element');
      case 'pdf-open':
        return t('learningPackages.eventPdfOpen', 'Opened PDF');
      case 'audio-open':
        return t('learningPackages.eventAudioOpen', 'Played audio');
      case 'video-open':
        return t('learningPackages.eventVideoOpen', 'Watched video');
      case 'link-open':
        return t('learningPackages.eventLinkOpen', 'Opened link');
      case 'quiz-open':
        return t('learningPackages.eventQuizOpen', 'Opened quiz');
      case 'survey-open':
        return t('learningPackages.eventSurveyOpen', 'Opened survey');
      case 'training-profile-open':
        return t('learningPackages.eventProfileOpen', 'Opened simulation');
      case 'item-open':
        return t('learningPackages.eventItemOpen', 'Opened from package');
      default:
        return eventType;
    }
  };

  const openItem = (group) => {
    if (group.itemId == null) return;
    switch (group.itemType) {
      case PackageItemTypes.LearningPath:
        navigate(`/learning-path/${group.itemId}`);
        break;
      case PackageItemTypes.QuizCollection:
        navigate(`/quiz/${group.itemId}`);
        break;
      case PackageItemTypes.TrainingProfile:
        navigate(`/simulations/heat-pump-training?profileId=${group.itemId}`);
        break;
      default:
        break;
    }
  };

  // Groups arrive newest-first from the API; events inside groups stay chronological.
  const orderedGroups = (groups || []).slice(0, maxGroups);
  if (order === 'oldest') {
    orderedGroups.reverse();
  }

  if (orderedGroups.length === 0) {
    return <div className={styles.empty}>{t('learningPackages.noActivityYet', 'No activity yet')}</div>;
  }

  return (
    <Timeline
      items={orderedGroups.map((group) => {
        const clickable = group.itemId != null;
        const groupKey = `${group.itemType}-${group.itemId}-${group.startedAt}`;
        const events = group.events || [];
        const isExpanded = expandedGroupKeys.has(groupKey);
        const visibleEvents = isExpanded ? events : events.slice(0, maxEventsPerGroup);
        const hiddenCount = events.length - maxEventsPerGroup;
        return {
          color: TYPE_COLORS[group.itemType] || 'gray',
          children: (
            <div>
              <Tooltip title={clickable ? t('learningPackages.timelineOpenHint', 'Open and play') : null}>
                <div
                  className={`${styles.groupHeader} ${clickable ? '' : styles.groupHeaderStatic}`}
                  onClick={clickable ? () => openItem(group) : undefined}
                >
                  <span className={styles.groupTypeIcon}>{typeIcon(group.itemType)}</span>
                  <span className={styles.groupName}>{group.itemName}</span>
                  {clickable && <PlayCircleOutlined className={styles.groupOpenIcon} />}
                  <span className={styles.groupDate}>{new Date(group.endedAt).toLocaleDateString()}</span>
                </div>
              </Tooltip>
              {visibleEvents.map((event, eventIndex) => (
                <div key={eventIndex} className={styles.eventRow}>
                  <span className={styles.eventIcon}>{eventIcon(event.eventType)}</span>
                  <span>{eventLabel(event.eventType)}</span>
                  {event.detail && <span className={styles.eventDetail}>{event.detail}</span>}
                  {event.scorePercent != null && (
                    <Tag color="gold" style={{ marginLeft: 4 }}>{event.scorePercent}%</Tag>
                  )}
                  <span className={styles.eventTime}>
                    {new Date(event.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
              {hiddenCount > 0 && (
                <Button
                  type="link"
                  size="small"
                  className={styles.moreEvents}
                  onClick={() => toggleExpanded(groupKey)}
                >
                  {isExpanded
                    ? t('learningPackages.showLessSteps', 'Show less')
                    : t('learningPackages.showAllSteps', {
                        count: hiddenCount,
                        defaultValue: '+ {{count}} more steps — show all',
                      })}
                </Button>
              )}
            </div>
          ),
        };
      })}
    />
  );
};

PackageTimeline.propTypes = {
  groups: PropTypes.array,
  maxGroups: PropTypes.number,
  maxEventsPerGroup: PropTypes.number,
  order: PropTypes.oneOf(['newest', 'oldest']),
};

export default PackageTimeline;
